import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AnnaAvatar } from '../../avatar/AnnaAvatar';
import { AgendaArc } from './AgendaArc';
import { OrbitCards } from './OrbitCards';
import { Satellites } from './Satellites';
import { SatelliteThread } from './SatelliteThread';
import { CaptionArea } from '../CaptionArea';
import { ProposalFocus } from '../ProposalFocus';
import { AlarmCard } from '../cards/AlarmCard';
import { ORBIT_GAP, type WallGeometry } from '../../../utils/orbitLayout';
import { agendaLabels } from '../../../utils/agendaGeometry';
import { agendaToday, nextEvent } from '../../../utils/dbSelectors';
import { useDb } from '../../../hooks/useDb';
import type { AgentId } from '../../../types/anna';
import type { AnnaStage } from '../useAnnaStage';
interface OrbitSceneProps {
  stage: AnnaStage;
  g: WallGeometry;
  nothingGoingOn: boolean;
  listening: boolean;
}
const EASE = [0.23, 1, 0.32, 1] as const;
const FOCUS_BOTTOM = 150;

/** The living stage: Anna fills the room; cards, the agenda arc and agent satellites orbit her. */
export function OrbitScene({
  stage,
  g,
  nothingGoingOn,
  listening
}: OrbitSceneProps) {
  const [thread, setThread] = useState<{
    agent: AgentId;
    x: number;
    y: number;
  } | null>(null);
  const orbit = stage.view === 'stage';
  const approvals = stage.view === 'approvals';
  const wideCaption = approvals || stage.view === 'alarm';
  useEffect(() => {
    if (!orbit || stage.stopped) setThread(null);
  }, [orbit, stage.stopped]);
  const closeThread = useCallback(() => setThread(null), []);
  const hasAgenda = orbit && stage.cards.some(c => c.kind === 'vandaag');
  /** Slapen: only time, the dimmed avatar, mic and Noodstop. Satellites return when the house wakes. */
  const asleep = stage.view === 'sleep';
  const houseAsleep = stage.mode === 'Slapen';
  const agenda = useDb(t => ({
    events: agendaToday(t),
    next: nextEvent(t)
  }));
  const obstacles = useMemo(() => hasAgenda ? agendaLabels(agenda.events, g.R, agenda.next?.id).map(l => l.box) : [], [hasAgenda, agenda, g.R]);
  const captionW = wideCaption ? Math.min(820, g.w - 80) : Math.min(880, 2 * (g.R * ORBIT_GAP + 20) - 48);
  const captionTop = g.cy + g.R + (approvals ? 10 : 22);
  const focusW = Math.min(900, g.w - 80);
  const fromY = g.cy - (g.h - FOCUS_BOTTOM - 220);
  return <>
      <motion.div aria-hidden className="pointer-events-none absolute left-0 top-0" style={{
      width: g.canvas,
      height: g.canvas,
      zIndex: listening ? 13 : 1
    }} initial={false} animate={{
      x: g.cx - g.canvas / 2,
      y: g.cy - g.canvas / 2,
      scale: g.scale
    }} transition={{
      duration: 0.3,
      ease: EASE
    }} data-id="element-1660" data-mp="3uklves94mnpt">
        <AnnaAvatar state={stage.avatarState} pulseKey={stage.pulseKey} data-id="element-1661" data-mp="3fqqqtyg07kkf" />
      </motion.div>

      <AnimatePresence data-id="element-1662" data-mp="2967p03obvw6n">{hasAgenda && <AgendaArc key="agenda" cx={g.cx} cy={g.cy} R={g.R} data-id="element-1663" data-mp="249h85e4p8wk6" />}</AnimatePresence>

      {orbit && !houseAsleep && <Satellites cx={g.cx} cy={g.cy} R={g.R} stopped={stage.stopped} dim={stage.quiet} flash={stage.flash} openAgent={thread?.agent ?? null} onOpen={(agent, x, y) => setThread(t => t?.agent === agent ? null : {
      agent,
      x,
      y
    })} data-id="element-1664" data-mp="jrycsa6k7zy7" />}

      <OrbitCards g={g} cards={orbit ? stage.cards : []} obstacles={obstacles} onRemove={stage.removeCard} onApproved={stage.notifyApproved} onSound={stage.sound} data-id="element-1665" data-mp="yhnq13rxfueu" />

      <AnimatePresence data-id="element-1666" data-mp="17iu83ha85da7">
        {thread && !houseAsleep && <SatelliteThread key={thread.agent} agent={thread.agent} x={thread.x} y={thread.y} g={g} onClose={closeThread} data-id="element-1667" data-mp="1ch5lg1zqa5x6" />}
      </AnimatePresence>

      {!asleep && <div className="absolute z-[14] flex flex-col items-center gap-6 text-center" style={{
      left: g.cx - captionW / 2,
      top: captionTop,
      width: captionW
    }} data-id="element-1668" data-mp="3e80jz1lbkj7l">
          <CaptionArea stage={stage} nothingGoingOn={nothingGoingOn} compact={approvals || stage.quiet} data-id="element-1669" data-mp="1dhi5ji61kjcn" />
          <AnimatePresence data-id="element-1670" data-mp="18jgf2c6adhr5">{stage.view === 'alarm' && <AlarmCard key="alarm" onAck={stage.ackAlarm} data-id="element-1671" data-mp="18iornomueqby" />}</AnimatePresence>
        </div>}

      <AnimatePresence data-id="element-1672" data-mp="1mhwssn2kg8is">
        {approvals && <motion.div key="focus" className="absolute z-20" style={{
        left: g.cx - focusW / 2,
        bottom: FOCUS_BOTTOM,
        width: focusW
      }} exit={{
        opacity: 0,
        transition: {
          duration: 0.2
        }
      }} data-id="element-1673" data-mp="1igl20e7yhh90">
            <ProposalFocus pending={stage.pending} outcomes={stage.outcomes} onApprove={stage.approve} onReject={stage.reject} onEdit={() => stage.showToast('Aanpassen kan op je bureauscherm of telefoon.', 'meldt')} fromY={fromY} compact={g.h < 900} data-id="element-1674" data-mp="pua275s6bkd9" />
          </motion.div>}
      </AnimatePresence>
    </>;
}